import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { LoadingButton } from "~/components/loading-button";
import { Close as CloseIcon } from "@mui/icons-material";
import { pluralize } from "~/lib/pluralize";
import { DialogProps } from "@toolpad/core";
import { useRemoveExceptionsGroupDialog } from "~/components/exceptions/hooks/useRemoveExceptionsGroupDialog";
import { ReactNode } from "react";

export interface RemoveExceptionsGroupDialogPayload {
  onRemove: ({
    exceptionMrns,
    successMessage,
    errorMessage,
  }: {
    exceptionMrns: string[];
    successMessage?: string;
    errorMessage?: string;
  }) => Promise<void>;
  exceptionMrns: string[];
  title?: string;
  message?: ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  successMessage?: string;
  errorMessage?: string;
}

export interface RemoveExceptionsGroupDialogProps
  extends DialogProps<RemoveExceptionsGroupDialogPayload> {}

export const RemoveExceptionsGroupDialog = (
  props: RemoveExceptionsGroupDialogProps,
) => {
  const {
    handleClose,
    handleRemoveExceptionsGroupClick,
    exceptionMrns,
    isLoading,
  } = useRemoveExceptionsGroupDialog(props);

  const exceptionCount = Math.max(exceptionMrns.length, 1);
  const isMultiple = exceptionCount > 1;
  const title = props.payload.title ?? "ARE YOU SURE?";
  const message = props.payload.message ?? (
    <>
      Are you sure you want to remove{" "}
      {isMultiple ? `${exceptionCount} exceptions` : "this exception"} and
      enable the {pluralize("finding", exceptionCount)}?
    </>
  );
  const confirmButtonText =
    props.payload.confirmButtonText ??
    `Yes, Remove ${isMultiple ? `${exceptionCount} Exceptions` : "the Exception"} and enable the ${pluralize("finding", exceptionCount)}`;
  const cancelButtonText =
    props.payload.cancelButtonText ??
    `No, keep the ${pluralize("exception", exceptionCount)}`;

  return (
    <Dialog onClose={handleClose} open={props?.open} fullWidth maxWidth="sm">
      <DialogTitle
        component="div"
        data-test-id="confirm-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1,
          mx: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textTransform: "uppercase",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
          }}
        >
          {title}
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogActions
        sx={{
          flexDirection: "column",
          justifyContent: "center",
          pb: 5,
          rowGap: 2,
        }}
      >
        <Typography color="textSecondary" sx={{ mb: 1 }}>
          {message}
        </Typography>
        <LoadingButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={isLoading}
          loading={isLoading}
          buttonText={confirmButtonText}
          onClick={handleRemoveExceptionsGroupClick}
          data-test-id="exception-delete-confirm-button"
        />

        <Button
          variant="text"
          sx={{ display: "inline-block", marginLeft: "0 !important" }}
          onClick={handleClose}
          disabled={isLoading}
        >
          {cancelButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
